/*----------------------------------
- HOME: THE DEMO STRIP'S FIGURES
----------------------------------*/

/**
 * The four figures under Home's "Find a name" rows (mock parity web-frame-12, Gaetan's default 6 of 2026-11-03): the
 * search's name count, its open and aftermarket shares and its median ask, every one read from what Product already
 * serves for the bare `/domains` search (`stats.count`, `stats.insights.statusMix`, `stats.medianAsk`). No new public
 * figure and no ADR-0028 change: the mock's register-from price, under-value count, median upside and top words are
 * not drawn (they would be new public aggregates).
 *
 * @docs docs/features/website-landing-experience
 * @adr  ADR-0028
 * @rule A share is one served status count over the served status counts, the one figure the browser derives here:
 *       presentation of returned counts, as a chart of them would be, and the option Gaetan accepted (default 6 of
 *       2026-11-03, "the open and aftermarket shares from the served status mix"): the `available` status is worded "open" and `resell` "aftermarket", as the mock words its strip;
 *       no status is grouped with another here. The swatches before the count are the same served counts drawn as one
 *       bar. A figure Product did not send drops its part rather than printing a zero.
 */

/*----------------------------------
- DEPENDANCES
----------------------------------*/

import type { WebsiteDomainsStats } from '@/common/types/domains/websiteLanding';

/*----------------------------------
- TYPES
----------------------------------*/

export type HomeDemoStripFigures = {
    /** The served count, or null when absent. */
    count: number | null;
    /** Whole percents of the served status counts, or null when the mix is empty. */
    openPct: number | null;
    aftermarketPct: number | null;
    /** The served median ask in dollars, or null when absent. */
    medianAsk: number | null;
    /** The bar before the count: open, aftermarket, premium, then every other status, each a share of the served mix. */
    bar: readonly HomeDemoStripSegment[];
};

export type HomeDemoStripSegment = { id: 'open' | 'aftermarket' | 'premium' | 'other'; pct: number };

/*----------------------------------
- CONSTANTS
----------------------------------*/

/** The status-mix ids the two shares read (`statusMixCatalog.ts`): one served status each. */
const OPEN_STATUS_ID = 'available';
const AFTERMARKET_STATUS_ID = 'resell';
const PREMIUM_STATUS_ID = 'premium';

/*----------------------------------
- HELPERS
----------------------------------*/

const positiveOrNull = (value: number | null | undefined): number | null =>
    typeof value === 'number' && Number.isFinite(value) && value > 0 ? value : null;

/*----------------------------------
- PUBLIC API
----------------------------------*/

export const readHomeDemoStripFigures = (stats: WebsiteDomainsStats | null | undefined): HomeDemoStripFigures => {
    const mix = stats?.insights?.statusMix ?? [];
    const total = mix.reduce((sum, entry) => sum + (positiveOrNull(entry.count) ?? 0), 0);
    const share = (id: string): number | null => {
        if (total <= 0) return null;
        const count = positiveOrNull(mix.find((entry) => entry.id === id)?.count) ?? 0;
        return Math.round((count / total) * 100);
    };
    const openPct = share(OPEN_STATUS_ID);
    const aftermarketPct = share(AFTERMARKET_STATUS_ID);
    const premiumPct = share(PREMIUM_STATUS_ID);
    const bar: HomeDemoStripSegment[] = openPct === null || aftermarketPct === null || premiumPct === null ? [] : [
        { id: 'open', pct: openPct },
        { id: 'aftermarket', pct: aftermarketPct },
        { id: 'premium', pct: premiumPct },
        // The rest of the mix, so the bar always spans the whole; rounding can leave it at 0 or 1.
        { id: 'other', pct: Math.max(0, 100 - openPct - aftermarketPct - premiumPct) },
    ];
    return {
        count: positiveOrNull(stats?.count),
        openPct,
        aftermarketPct,
        medianAsk: positiveOrNull(stats?.medianAsk),
        bar,
    };
};
